import type {RichTextShape} from './rich-text-schema';

/**
 * Whether two HTML texts render the same, for deciding if the editor can edit a stored text without
 * changing it (load it, write it back, compare): the editor writes the same thing in a different
 * spelling (`<br>` for `<br />`, characters for entities, attributes in its own order), so both
 * sides are reduced to a canonical form first. The canonical form keeps everything that renders:
 * the tags and their nesting, every attribute value, the text (with whitespace collapsed as HTML
 * collapses it, kept where a pre or white-space: pre* keeps it). It drops what does not: entity
 * spelling, attribute order, spacing inside a style attribute, empty inline elements, whitespace
 * between blocks and at their edges, and the split of a run of text across two identical
 * neighbouring elements (`<b>a</b><b>b</b>` is `<b>ab</b>`).
 *
 * In a paragraph-based text, inline content outside a paragraph counts as being in one, as the
 * editor puts it in one when it loads it, as CKEditor did.
 */
export const sameHtml = (a: string, b: string, shape: RichTextShape): boolean =>
  canonicalHtml(a, shape) === canonicalHtml(b, shape);

const BLOCK_TAGS = new Set([
  'address', 'article', 'aside', 'blockquote', 'dd', 'details', 'dialog', 'div', 'dl', 'dt', 'fieldset',
  'figcaption', 'figure', 'footer', 'form', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'header', 'hr', 'li',
  'main', 'nav', 'ol', 'p', 'pre', 'section', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr', 'ul',
]);
const VOID_TAGS = new Set(['br', 'img', 'hr', 'wbr', 'input', 'meta', 'link', 'col', 'source']);

type Canonical = {tag: string; attributes: string; children: Canonical[]} | string;

export const canonicalHtml = (html: string, shape: RichTextShape): string => {
  const template = document.createElement('template');
  template.innerHTML = html || '';
  let nodes = trimEdges(canonicalChildren(template.content, false));
  if (shape === 'blocks') {
    nodes = wrapLooseInline(nodes);
  }
  return nodes.map(write).join('');
};

const canonicalChildren = (parent: ParentNode, keepWhitespace: boolean): Canonical[] => {
  const out: Canonical[] = [];
  for (const child of Array.from(parent.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = keepWhitespace ? child.textContent : child.textContent.replace(/[ \t\r\n\f]+/g, ' ');
      if (text) {
        out.push(text);
      }
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const element = child as Element;
      const tag = element.tagName.toLowerCase();
      const keep = keepWhitespace || tag === 'pre' || /white-space\s*:\s*pre/i.test(element.getAttribute('style') || '');
      const children = canonicalChildren(element, keep);
      const node = {
        tag,
        attributes: canonicalAttributes(element),
        children: BLOCK_TAGS.has(tag) && !keep ? trimEdges(children) : children,
      };
      if (!VOID_TAGS.has(tag) && !BLOCK_TAGS.has(tag) && !hasContent(node)) {
        continue;
      }
      out.push(node);
    } else if (child.nodeType === Node.COMMENT_NODE) {
      out.push({tag: '!--', attributes: child.textContent, children: []});
    }
  }
  return tidy(out, keepWhitespace);
};

const canonicalAttributes = (element: Element): string =>
  Array.from(element.attributes)
    .map((attribute) => {
      const value = attribute.name === 'style' ? canonicalStyle(attribute.value) : attribute.value;
      return `${attribute.name}=${JSON.stringify(value)}`;
    })
    .sort()
    .join(' ');

/** `color:#FFF;font-size: 12px` and `color: #FFF; font-size:12px;` are the same style. */
const canonicalStyle = (style: string): string =>
  style.split(';').map((declaration) => {
    const at = declaration.indexOf(':');
    return at < 0 ? declaration.trim() : `${declaration.slice(0, at).trim().toLowerCase()}:${declaration.slice(at + 1).trim()}`;
  }).filter(Boolean).join(';');

const hasContent = (node: Canonical): boolean =>
  typeof node === 'string'
    ? node.length > 0
    : VOID_TAGS.has(node.tag) || node.tag === '!--' || node.children.some(hasContent);

const isBlock = (node: Canonical | undefined): boolean => typeof node !== 'string' && !!node && BLOCK_TAGS.has(node.tag);

/**
 * Joins neighbouring text and neighbouring identical inline elements, and drops whitespace between
 * blocks. Text that collapses whitespace collapses it across the join too.
 */
const tidy = (nodes: Canonical[], keepWhitespace: boolean): Canonical[] => {
  const out: Canonical[] = [];
  for (const node of nodes) {
    const last = out[out.length - 1];
    if (typeof node === 'string' && typeof last === 'string') {
      out[out.length - 1] = keepWhitespace ? last + node : (last + node).replace(/ {2,}/g, ' ');
    } else if (typeof node !== 'string' && typeof last !== 'string' && last && !isBlock(node)
      && !VOID_TAGS.has(node.tag) && last.tag === node.tag && last.attributes === node.attributes) {
      out[out.length - 1] = {...last, children: tidy([...last.children, ...node.children], keepWhitespace)};
    } else {
      out.push(node);
    }
  }
  if (!keepWhitespace) {
    // A line's leading and trailing collapsible space does not render either: `a <br> b` is `a<br>b`.
    out.forEach((node, index) => {
      if (typeof node === 'string') {
        const isBr = (neighbour: Canonical | undefined) => typeof neighbour !== 'string' && neighbour?.tag === 'br';
        out[index] = node.replace(isBr(out[index - 1]) ? /^ / : /^$/, '').replace(isBr(out[index + 1]) ? / $/ : /^$/, '');
      }
    });
  }
  return out.filter((node, index) => {
    if (node === '') {
      return false;
    }
    if (typeof node !== 'string' || node.trim() || keepWhitespace) {
      return true;
    }
    const before = out[index - 1];
    const after = out[index + 1];
    return !((!before || isBlock(before)) && (!after || isBlock(after)));
  });
};

/** A block's leading and trailing collapsible space does not render. */
const trimEdges = (nodes: Canonical[]): Canonical[] => {
  const out = [...nodes];
  if (typeof out[0] === 'string') {
    out[0] = (out[0] as string).replace(/^ /, '');
  }
  const end = out.length - 1;
  if (typeof out[end] === 'string') {
    out[end] = (out[end] as string).replace(/ $/, '');
  }
  return out.filter((node) => node !== '');
};

const wrapLooseInline = (nodes: Canonical[]): Canonical[] => {
  const out: Canonical[] = [];
  let run: Canonical[] = [];
  const flush = () => {
    if (run.some((node) => typeof node !== 'string' || node.trim())) {
      out.push({tag: 'p', attributes: '', children: trimEdges(tidy(run, false))});
    }
    run = [];
  };
  for (const node of nodes) {
    if (isBlock(node)) {
      flush();
      out.push(node);
    } else {
      run.push(node);
    }
  }
  flush();
  return out;
};

const write = (node: Canonical): string =>
  typeof node === 'string'
    ? JSON.stringify(node)
    : `<${node.tag} ${node.attributes}>${node.children.map(write).join('')}</${node.tag}>`;

/** Text with HTML's special characters escaped, for putting plain text into HTML. */
export const escapeHtmlText = (text: string): string =>
  String(text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
